import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../config/api";

function AdminDashboard() {
  /* ================= AUTH (ADMIN ONLY) ================= */
  let token = null;
  let user = null;

  try {
    token = localStorage.getItem("token");
    user = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    window.location.href = "/login";
    return null;
  }

  if (!token || !user || user.role !== "admin") {
    window.location.href = "/login";
    return null;
  }

  const authHeaders = {
    Authorization: `Bearer ${token}`
  };

  /* ================= TOAST ================= */
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success"
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  /* ================= FACULTY ================= */
  const [facultyList, setFacultyList] = useState([]);
  const [faculty, setFaculty] = useState({
    name: "",
    department: "",
    designation: "",
    image: null
  });

  const loadFaculty = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/faculty`);
      setFacultyList(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const addFaculty = async (e) => {
    e.preventDefault();

    if (!faculty.name.trim()) {
      showToast("Faculty name is required", "error");
      return;
    }

    const formData = new FormData();
    formData.append("name", faculty.name);
    formData.append("department", faculty.department || "");
    formData.append("designation", faculty.designation || "");
    if (faculty.image) {
      formData.append("image", faculty.image);
    }

    try {
      await axios.post(
        `${API_BASE_URL}/faculty`,
        formData,
        { headers: authHeaders }
      );

      showToast("Faculty added successfully");

      setFaculty({
        name: "",
        department: "",
        designation: "",
        image: null
      });

      document.getElementById("facultyImage").value = "";
      loadFaculty();
    } catch (err) {
      console.error(err);
      showToast("Failed to add faculty", "error");
    }
  };

  const deleteFaculty = async (id) => {
    if (!window.confirm("Delete this faculty?")) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/faculty/${id}`,
        { headers: authHeaders }
      );
      showToast("Faculty deleted", "error");
      loadFaculty();
    } catch (err) {
      console.error(err);
      showToast("Delete failed", "error");
    }
  };

  /* ================= GALLERY ================= */
  const [galleryImages, setGalleryImages] = useState([]);

  const uploadGallery = async (e) => {
    e.preventDefault();

    if (galleryImages.length === 0) {
      showToast("Select images first", "error");
      return;
    }

    if (galleryImages.length > 5) {
      showToast("Maximum 5 images allowed", "error");
      return;
    }

    try {
      for (const img of galleryImages) {
        const formData = new FormData();
        formData.append("image", img);

        await axios.post(
          `${API_BASE_URL}/gallery`,
          formData,
          { headers: authHeaders }
        );
      }

      showToast("Gallery images uploaded");
      setGalleryImages([]);
      document.getElementById("galleryImages").value = "";
    } catch (err) {
      console.error(err);
      showToast("Gallery upload failed", "error");
    }
  };

  /* ================= CHAIRMAN ================= */
  const [chairman, setChairman] = useState({
    message: "",
    image: null
  });

  const saveChairman = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("message", chairman.message || "");
    if (chairman.image) formData.append("image", chairman.image);

    try {
      await axios.post(
        `${API_BASE_URL}/messages/chairman`,
        formData,
        { headers: authHeaders }
      );
      showToast("Chairman message saved");
    } catch {
      showToast("Failed to save chairman message", "error");
    }
  };

  /* ================= PRINCIPAL ================= */
  const [principal, setPrincipal] = useState({
    message: "",
    image: null
  });

  const savePrincipal = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("message", principal.message || "");
    if (principal.image) formData.append("image", principal.image);

    try {
      await axios.post(
        `${API_BASE_URL}/messages/principal`,
        formData,
        { headers: authHeaders }
      );
      showToast("Principal message saved");
    } catch {
      showToast("Failed to save principal message", "error");
    }
  };

  useEffect(() => {
    loadFaculty();
  }, []);

  return (
    <div className="admin-container">
      <h1 className="admin-title">Admin Dashboard</h1>

      {toast.show && (
        <div className={`toast ${toast.type}`}>{toast.message}</div>
      )}

      <div className="admin-grid">

        {/* FACULTY */}
        <div className="admin-card">
          <h2>Faculty Management</h2>

          <form onSubmit={addFaculty}>
            <input
              placeholder="Name"
              value={faculty.name}
              onChange={(e) =>
                setFaculty({ ...faculty, name: e.target.value })
              }
            />
            <input
              placeholder="Department"
              value={faculty.department}
              onChange={(e) =>
                setFaculty({ ...faculty, department: e.target.value })
              }
            />
            <input
              placeholder="Designation"
              value={faculty.designation}
              onChange={(e) =>
                setFaculty({ ...faculty, designation: e.target.value })
              }
            />
            <input
              type="file"
              id="facultyImage"
              onChange={(e) =>
                setFaculty({ ...faculty, image: e.target.files[0] })
              }
            />
            <button>Add Faculty</button>
          </form>

          <ul>
            {facultyList.map((f) => (
              <li key={f.id}>
                {f.name}
                <button onClick={() => deleteFaculty(f.id)}>❌</button>
              </li>
            ))}
          </ul>
        </div>

        {/* GALLERY */}
        <div className="admin-card">
          <h2>Gallery Management</h2>

          <form onSubmit={uploadGallery}>
            <input
              type="file"
              id="galleryImages"
              multiple
              accept="image/*"
              onChange={(e) =>
                setGalleryImages(Array.from(e.target.files))
              }
            />
            <small>Max 5 images</small>
            <button>Upload Images</button>
          </form>
        </div>

        {/* CHAIRMAN */}
        <div className="admin-card">
          <h2>Chairman Message</h2>
          <form onSubmit={saveChairman}>
            <textarea
              placeholder="Message"
              onChange={(e) =>
                setChairman({ ...chairman, message: e.target.value })
              }
            />
            <input
              type="file"
              onChange={(e) =>
                setChairman({ ...chairman, image: e.target.files[0] })
              }
            />
            <button>Save</button>
          </form>
        </div>

        {/* PRINCIPAL */}
        <div className="admin-card">
          <h2>Principal Message</h2>
          <form onSubmit={savePrincipal}>
            <textarea
              placeholder="Message"
              onChange={(e) =>
                setPrincipal({ ...principal, message: e.target.value })
              }
            />
            <input
              type="file"
              onChange={(e) =>
                setPrincipal({ ...principal, image: e.target.files[0] })
              }
            />
            <button>Save</button>
          </form>
        </div>

      </div>
    </div>
  );
}

export default AdminDashboard;







// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import API_BASE_URL from "../config/api";

// function AdminDashboard() {
//   /* ================= AUTH CHECK ================= */
//   const token = localStorage.getItem("token");
//   const userStr = localStorage.getItem("user");

//   let user = null;
//   try {
//     user = JSON.parse(userStr);
//   } catch {
//     user = null;
//   }

//   if (!token || !user || user.role !== "admin") {
//     window.location.href = "/login";
//     return null;
//   }

//   /* ================= TOAST ================= */
//   const [toast, setToast] = useState({
//     show: false,
//     message: "",
//     type: "success"
//   });

//   const showToast = (message, type = "success") => {
//     setToast({ show: true, message, type });
//     setTimeout(() => {
//       setToast({ show: false, message: "", type: "" });
//     }, 3000);
//   };

//   /* ================= FACULTY ================= */
//   const [facultyList, setFacultyList] = useState([]);
//   const [faculty, setFaculty] = useState({
//     name: "",
//     department: "",
//     designation: "",
//     image: null
//   });

//   const loadFaculty = async () => {
//     try {
//       const res = await axios.get(`${API_BASE_URL}/faculty`);
//       setFacultyList(res.data);
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const addFaculty = async (e) => {
//     e.preventDefault();

//     if (!faculty.name.trim()) {
//       showToast("Faculty name is required", "error");
//       return;
//     }

//     const formData = new FormData();
//     formData.append("name", faculty.name);
//     formData.append("department", faculty.department || "");
//     formData.append("designation", faculty.designation || "");
//     if (faculty.image) {
//       formData.append("image", faculty.image);
//     }

//     try {
//       await axios.post(
//         `${API_BASE_URL}/faculty`,
//         formData,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       showToast("Faculty added successfully");

//       setFaculty({
//         name: "",
//         department: "",
//         designation: "",
//         image: null
//       });

//       document.getElementById("facultyImage").value = "";
//       loadFaculty();
//     } catch (err) {
//       console.error(err.response?.data || err);
//       showToast("Failed to add faculty", "error");
//     }
//   };

//   const deleteFaculty = async (id) => {
//     if (!window.confirm("Delete this faculty?")) return;

//     try {
//       await axios.delete(
//         `${API_BASE_URL}/faculty/${id}`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`
//           }
//         }
//       );

//       showToast("Faculty deleted", "error");
//       loadFaculty();
//     } catch (err) {
//       console.error(err);
//       showToast("Delete failed", "error");
//     }
//   };

//   /* ================= GALLERY ================= */
//   const [galleryImages, setGalleryImages] = useState([]);

//   const uploadGallery = async (e) => {
//     e.preventDefault();

//     if (galleryImages.length === 0) {
//       showToast("Please select images", "error");
//       return;
//     }

//     try {
//       for (const img of galleryImages) {
//         const formData = new FormData();
//         formData.append("image", img);

//         await axios.post(
//           `${API_BASE_URL}/gallery`,
//           formData,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`
//             }
//           }
//         );
//       }

//       showToast("Gallery images uploaded successfully");
//       setGalleryImages([]);
//       document.getElementById("galleryImages").value = "";
//     } catch (err) {
//       console.error(err.response?.data || err);
//       showToast("Image upload failed", "error");
//     }
//   };

//   /* ================= CHAIRMAN ================= */
//   const [chairman, setChairman] = useState({ message: "", image: null });

//   const saveChairman = async (e) => {
//     e.preventDefault();

//     const formData = new FormData();
//     formData.append("message", chairman.message || "");
//     if (chairman.image) formData.append("image", chairman.image);

//     await axios.post(
//       `${API_BASE_URL}/messages/chairman`,
//       formData,
//       {
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       }
//     );

//     showToast("Chairman message saved");
//   };

//   /* ================= PRINCIPAL ================= */
//   const [principal, setPrincipal] = useState({ message: "", image: null });

//   const savePrincipal = async (e) => {
//     e.preventDefault();

//     const formData = new FormData();
//     formData.append("message", principal.message || "");
//     if (principal.image) formData.append("image", principal.image);

//     await axios.post(
//       `${API_BASE_URL}/messages/principal`,
//       formData,
//       {
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       }
//     );

//     showToast("Principal message saved");
//   };

//   useEffect(() => {
//     loadFaculty();
//   }, []);

//   return (
//     <div className="admin-container">
//       <h1 className="admin-title">Admin Dashboard</h1>

//       {toast.show && (
//         <div className={`toast ${toast.type}`}>
//           {toast.message}
//         </div>
//       )}

//       <div className="admin-grid">

//         {/* FACULTY */}
//         <div className="admin-card">
//           <h2>Faculty Management</h2>

//           <form onSubmit={addFaculty}>
//             <input
//               placeholder="Name"
//               value={faculty.name}
//               onChange={(e) => setFaculty({ ...faculty, name: e.target.value })}
//             />

//             <input
//               placeholder="Department"
//               value={faculty.department}
//               onChange={(e) => setFaculty({ ...faculty, department: e.target.value })}
//             />

//             <input
//               placeholder="Designation"
//               value={faculty.designation}
//               onChange={(e) => setFaculty({ ...faculty, designation: e.target.value })}
//             />

//             <input
//               type="file"
//               id="facultyImage"
//               onChange={(e) =>
//                 setFaculty({ ...faculty, image: e.target.files[0] })
//               }
//             />

//             <button>Add Faculty</button>
//           </form>

//           <ul>
//             {facultyList.map((f) => (
//               <li key={f.id}>
//                 {f.name}
//                 <button onClick={() => deleteFaculty(f.id)}>❌</button>
//               </li>
//             ))}
//           </ul>
//         </div>

//         {/* GALLERY */}
//         <div className="admin-card">
//           <h2>Gallery Management</h2>

//           <form onSubmit={uploadGallery}>
//             <input
//               type="file"
//               id="galleryImages"
//               multiple
//               accept="image/*"
//               onChange={(e) =>
//                 setGalleryImages(Array.from(e.target.files))
//               }
//             />
//             <button>Upload Images</button>
//           </form>
//         </div>

//         {/* CHAIRMAN */}
//         <div className="admin-card">
//           <h2>Chairman Message</h2>

//           <form onSubmit={saveChairman}>
//             <textarea
//               placeholder="Message"
//               onChange={(e) =>
//                 setChairman({ ...chairman, message: e.target.value })
//               }
//             />
//             <input
//               type="file"
//               onChange={(e) =>
//                 setChairman({ ...chairman, image: e.target.files[0] })
//               }
//             />
//             <button>Save</button>
//           </form>
//         </div>

//         {/* PRINCIPAL */}
//         <div className="admin-card">
//           <h2>Principal Message</h2>

//           <form onSubmit={savePrincipal}>
//             <textarea
//               placeholder="Message"
//               onChange={(e) =>
//                 setPrincipal({ ...principal, message: e.target.value })
//               }
//             />
//             <input
//               type="file"
//               onChange={(e) =>
//                 setPrincipal({ ...principal, image: e.target.files[0] })
//               }
//             />
//             <button>Save</button>
//           </form>
//         </div>

//       </div>
//     </div>
//   );
// }

// export default AdminDashboard;
